/**
 * examPlan · генератор плана подготовки к ISTQB для «штаба» S06
 * (slot_s06_rework · W5 · 11 JUN 2026 · мокап 04_DESIGN/mockup_s06_hq.html).
 *
 * План — ЧИСТАЯ ФУНКЦИЯ от (дата экзамена, сегодня, прогресс): ничего не
 * хранится, после каждого мока/сессии план «тихо пересобирается, без красного
 * и без чувства вины» (СДВГ-дружелюбие — обещание из мокапа, ack Genie).
 * Прошедшие дни не реконструируются — показываем только сегодня → день экзамена.
 */
import type { Progress } from '@/types';

export type PlanTaskKind = 'ticket' | 'mock' | 'glossary' | 'drill' | 'review' | 'rest';

export interface PlanTask {
  id: string;
  label: string;
  kind: PlanTaskKind;
  /** Тикет, который открывает задача (kind='ticket'). */
  ticketId?: string;
  /** Слот мока (kind='mock'). */
  mockSlot?: 'mock1' | 'mock2';
  /** Выполнено — выводится из прогресса (моки/тикеты); эфемерные задачи не трекаются. */
  done: boolean;
}

export interface PlanDay {
  /** 0 = сегодня · N = дней от сегодня. */
  offset: number;
  /** Подпись: «сегодня» / «завтра» / «D-K» (K дней до экзамена). */
  label: string;
  tasks: PlanTask[];
}

const ticketDone = (p: Progress, id: string): boolean => p.tickets[id]?.status === 'done';

/** Сегодняшняя глоссарий-сессия «сделана», если сегодня было ≥10 повторений. */
function glossaryDoneToday(p: Progress, now: Date): boolean {
  const today = now.toISOString().slice(0, 10);
  let n = 0;
  for (const st of Object.values(p.cockpit.flashcards)) {
    if (st.lastReviewAt && st.lastReviewAt.slice(0, 10) === today) n += 1;
    if (n >= 10) return true;
  }
  return false;
}

/**
 * Построить план от сегодня до дня экзамена включительно.
 * @param examDate ISO-дата экзамена (cockpit.examDate)
 * @param progress прогресс студента
 * @param now «сегодня» (инъекция для тестов)
 */
export function buildExamPlan(examDate: string, progress: Progress, now = new Date()): PlanDay[] {
  const exam = new Date(examDate + 'T12:00:00');
  const today = new Date(now);
  today.setHours(12, 0, 0, 0);
  const totalDays = Math.max(0, Math.round((exam.getTime() - today.getTime()) / 86400_000));

  const mock1 = progress.cockpit.mockHistory.mock1;
  const mock2 = progress.cockpit.mockHistory.mock2;
  const mock2Passed = !!mock2 && mock2.correct >= 28;

  const days: PlanDay[] = [];
  const dayLabel = (offset: number): string => {
    if (offset === 0) return 'сегодня';
    if (offset === 1) return 'завтра';
    const left = totalDays - offset;
    return left === 0 ? 'экзамен' : `D-${left}`;
  };

  // Опорные точки: Mock#1 — как можно раньше; Mock#2 — за ~3 дня до экзамена
  // (при коротком сроке сдвигается, но не в день Mock#1); D-1 — лайт-день.
  const mock1Offset = mock1 ? -1 : 0;
  const mock2Offset = mock2Passed
    ? -1
    : Math.max(mock1 ? 0 : 1, Math.min(totalDays - 3, totalDays - 1));

  const glossToday = glossaryDoneToday(progress, now);

  for (let offset = 0; offset <= totalDays; offset += 1) {
    const left = totalDays - offset;
    const tasks: PlanTask[] = [];
    const push = (t: Omit<PlanTask, 'id'>, key: string) =>
      tasks.push({ ...t, id: `d${offset}-${key}` });

    if (left === 0 && totalDays > 0) {
      // День экзамена.
      push({ label: 'реальный экзамен — ни пуха', kind: 'rest', done: false }, 'exam');
      days.push({ offset, label: dayLabel(offset), tasks });
      continue;
    }

    if (left === 1) {
      // D-1: только лайт.
      push({ label: '«Мои трудные» · 30 мин — и всё', kind: 'glossary', done: false }, 'hard');
      push({ label: 'сон 7-8 часов', kind: 'rest', done: false }, 'sleep');
      days.push({ offset, label: dayLabel(offset), tasks });
      continue;
    }

    // Вводные материалы — в первые дни, пока не пройдены.
    if (offset <= 1 && !ticketDone(progress, 'CTFL-01')) {
      push({ label: 'карта силлабуса', kind: 'ticket', ticketId: 'CTFL-01', done: false }, 't01');
    }
    if (offset <= 1 && !ticketDone(progress, 'CTFL-02')) {
      push({ label: 'глоссарий — как устроен тренажёр', kind: 'ticket', ticketId: 'CTFL-02', done: false }, 't02');
    }
    if (offset === mock2Offset - 1 && !ticketDone(progress, 'CTFL-04')) {
      push({ label: 'стратегия — 6 ловушек', kind: 'ticket', ticketId: 'CTFL-04', done: false }, 't04');
    }

    // Моки + их разбор.
    if (offset === mock1Offset) {
      push({ label: 'Mock #1 · 60 мин', kind: 'mock', mockSlot: 'mock1', done: !!mock1 }, 'm1');
      push({ label: 'разбор ошибок · фильтр «только ошибки»', kind: 'review', done: false }, 'm1r');
    } else if (offset === mock1Offset + 1 && !ticketDone(progress, 'CTFL-05')) {
      push({ label: 'AI-план повторения · Промпт 1', kind: 'ticket', ticketId: 'CTFL-05', done: false }, 't05');
    }
    if (offset === mock2Offset) {
      push({ label: 'Mock #2 · гейт 28/40', kind: 'mock', mockSlot: 'mock2', done: mock2Passed }, 'm2');
    }
    if (offset === mock2Offset + 1 || (mock2Passed && offset === 0 && !ticketDone(progress, 'CTFL-06'))) {
      push({ label: 'регистрация на экзамен · без оплаты', kind: 'ticket', ticketId: 'CTFL-06', done: false }, 't06');
    }

    // Ежедневная база: глоссарий-сессия + дрилл слабой главы (после первого мока).
    const deck =
      offset % 4 === 3 ? 'пары-путаницы' : offset % 4 === 2 ? '«Мои трудные»' : 'к повторению';
    push(
      { label: `глоссарий · сессия (${deck})`, kind: 'glossary', done: offset === 0 && glossToday },
      'gl',
    );
    if ((mock1 || progress.cockpit.blockExamResult) && offset !== mock1Offset && offset !== mock2Offset) {
      push({ label: 'дрилл слабой главы · 10 вопросов', kind: 'drill', done: false }, 'dr');
    }

    days.push({ offset, label: dayLabel(offset), tasks });
  }

  return days;
}

/** Свернуть длинный план для рендера: сегодня + 3 ближайших дня подробно,
 *  середина схлопывается в один агрегат, хвост (D-1 + экзамен) подробно. */
export function collapsePlan(days: PlanDay[]): Array<PlanDay | { collapsed: true; from: string; to: string; tasks: PlanTask[] }> {
  if (days.length <= 7) return days;
  const head = days.slice(0, 4);
  const tail = days.slice(-2);
  const middle = days.slice(4, -2);
  const middleTasks: PlanTask[] = [];
  const seen = new Set<string>();
  for (const d of middle) {
    for (const t of d.tasks) {
      const key = t.label.replace(/\(.*\)/, '');
      if (seen.has(key)) continue;
      seen.add(key);
      middleTasks.push(t);
    }
  }
  return [
    ...head,
    { collapsed: true, from: middle[0]!.label, to: middle[middle.length - 1]!.label, tasks: middleTasks },
    ...tail,
  ];
}
